/**
 * /sales — founder daily glance.
 *
 * Mobile-first founder view: yesterday's all-outlets net sales + same-day
 * comparisons, MTD pace + projected month-end, per-outlet growth in absolute
 * Rs (no %), MTD AOV per outlet, MTD ads spend, sync state.
 *
 * Reads marketing.mv_outlet_kpi_daily (synced from Petpooja via GH Actions).
 * Detail analytics (top items, hourly heatmap, channel mix) live under
 * the "Detail" tab.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authClient } from '@/lib/supabase/auth';
import {
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  Calendar,
  CalendarClock,
  ChevronDown,
  Check,
  Loader2,
  MapPin,
  Megaphone,
  Minus,
  Pencil,
  Target,
  Wallet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MotionCard } from '@/components/motion/MotionCard';
import { useDashboard } from '@/hooks/useDashboardContext';
import { useOutlets } from '@/hooks/useOutlets';
import { marketing } from '@/lib/supabase/marketing';
import { formatINRCompact, formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';

// Daily query is now split: recentQ (last ~75d for MTD + LM + SDLW/SDLM) and
// lyQ (LY same month ± a few days). Single 400-day fetch tripped the 3s
// anon-role statement_timeout and silently truncated LY rows.

interface DailyRow {
  outlet_id: string;
  date: string;
  net_sales: number | null;
  orders: number | null;
  covers: number | null;
  discount_total: number | null;
  addon_revenue: number | null;
  addon_orders: number | null;
  dinein_orders: number | null;
  delivery_orders: number | null;
  pickup_orders: number | null;
}

export function SalesGlance() {
  const { outletId, period, customRange } = useDashboard();
  const outletsAll = useOutlets();
  // Honour the global outlet selector. 'all' = pan-Flax; specific id =
  // narrow every per-outlet computation to that single outlet so the
  // hero / pace / city cards all reflect that scope.
  const outletsScope = useMemo(() => {
    if (outletId === 'all') return outletsAll;
    return {
      ...outletsAll,
      data: (outletsAll.data ?? []).filter((o) => o.id === outletId),
    };
  }, [outletId, outletsAll]);
  const outlets = outletsScope;
  // Anchor date — every MTD / yesterday / QTD calc pivots off this.
  // - Last month → end of last month
  // - Custom range → end date of the custom range
  // - Yesterday → yesterday
  // - Everything else → today
  const today = useMemo(
    () => anchorDateForPeriod(period, customRange?.end),
    [period, customRange?.end],
  );
  // Two narrow windows instead of one 400-day fetch. The anon role has a 3s
  // statement_timeout — a single 2k+ row pull through outlet_sales_v (UNION
  // of petpooja_orders + outlet_sales_live with NOT EXISTS dedup) often blew
  // through that and returned a truncated result, dropping LY rows.
  // Recent window covers MTD + last-month-MTD + yesterday + SDLW + SDLM.
  // LY window covers the same-month-last-year + month-before for QTD math.
  const recentSince = isoDate(addDays(today, -75));
  const lyAnchor = new Date(today.getFullYear() - 1, today.getMonth(), 1);
  const lySince = isoDate(addDays(lyAnchor, -32));
  const lyUntil = isoDate(new Date(today.getFullYear() - 1, today.getMonth() + 1, 0));

  const SELECT_COLS =
    'outlet_id, date, net_sales, orders, covers, discount_total, addon_revenue, addon_orders, dinein_orders, delivery_orders, pickup_orders';

  const recentQ = useQuery({
    queryKey: ['sales-glance-recent', recentSince],
    queryFn: async (): Promise<DailyRow[]> => {
      const { data, error } = await marketing
        .from('outlet_sales_v')
        .select(SELECT_COLS)
        .gte('date', recentSince)
        .order('date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as DailyRow[];
    },
    staleTime: 60_000,
    placeholderData: (prev: DailyRow[] | undefined) => prev,
  });

  const lyQ = useQuery({
    queryKey: ['sales-glance-ly', lySince, lyUntil],
    queryFn: async (): Promise<DailyRow[]> => {
      const { data, error } = await marketing
        .from('outlet_sales_v')
        .select(SELECT_COLS)
        .gte('date', lySince)
        .lte('date', lyUntil);
      if (error) throw error;
      return (data ?? []) as unknown as DailyRow[];
    },
    staleTime: 5 * 60_000, // LY data doesn't move
    placeholderData: (prev: DailyRow[] | undefined) => prev,
  });

  const daily = useMemo(
    () => ({
      data: [...(recentQ.data ?? []), ...(lyQ.data ?? [])],
      isLoading: recentQ.isLoading || lyQ.isLoading,
    }),
    [recentQ.data, lyQ.data, recentQ.isLoading, lyQ.isLoading],
  );

  const lastSync = useQuery({
    queryKey: ['sales-last-sync'],
    queryFn: async (): Promise<string | null> => {
      // public.petpooja_orders is the canonical source; freshness = its
      // most recent synced_at (inventory pipeline's last successful pull).
      // Exposed via marketing.outlets_last_sync_v (created in the same migration).
      const { data, error } = await marketing
        .from('petpooja_orders_last_sync_v')
        .select('last_synced_at')
        .maybeSingle();
      if (error) return null;
      return (data as { last_synced_at: string } | null)?.last_synced_at ?? null;
    },
    staleTime: 60_000,
  });

  // Sync-on-load: when today's outlet_sales_live row is missing or older than
  // 2 min, fire petpooja-sales-fetch so the dashboard shows fresh intraday
  // numbers without waiting on the 30-min cron tick.
  const qcGlance = useQueryClient();
  const todayLive = useQuery({
    queryKey: ['sales-today-live', today],
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await marketing
        .from('outlet_sales_live')
        .select('synced_at')
        .eq('date', today)
        .order('synced_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) return null;
      return (data as { synced_at: string } | null)?.synced_at ?? null;
    },
    staleTime: 30_000,
  });
  const syncMutation = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.functions.invoke('petpooja-sales-fetch', { body: {} });
      if (error) throw error;
    },
    onSuccess: () => {
      qcGlance.invalidateQueries({ queryKey: ['sales-glance'] });
      qcGlance.invalidateQueries({ queryKey: ['sales-last-sync'] });
      qcGlance.invalidateQueries({ queryKey: ['sales-today-live', today] });
    },
  });
  useEffect(() => {
    if (todayLive.isLoading || syncMutation.isPending) return;
    const last = todayLive.data ? Date.parse(todayLive.data) : 0;
    const stale = !last || Date.now() - last > 2 * 60 * 1000;
    if (stale) syncMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayLive.isLoading]);

  const stats = useMemo(
    () => computeStats(daily.data ?? [], today, outlets.data ?? [], outletsAll.data ?? []),
    [daily.data, today, outlets.data, outletsAll.data],
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <FreshnessBar
        lastSyncedAt={lastSync.data ?? null}
        liveSyncedAt={todayLive.data ?? null}
        syncing={syncMutation.isPending}
      />

      {daily.isLoading ? (
        <>
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
        </>
      ) : (
        <>
          {(() => {
            const outletName =
              outletId === 'all'
                ? null
                : (outletsAll.data ?? []).find((o) => o.id === outletId)?.name ?? outletId;
            return (
              <>
                <HeroToday stats={stats} outletName={outletName} />
                <FlaxMTDAllCities stats={stats} outletName={outletName} />
              </>
            );
          })()}
          <CityCards stats={stats} outlets={outlets.data ?? []} />
          <div className="grid gap-4 md:grid-cols-2">
            <MTDPaceCard stats={stats} />
            <QTDPaceCard stats={stats} />
          </div>
          <TargetsCard stats={stats} />
          <PerOutletGrowth stats={stats} outlets={outlets.data ?? []} />
          <div className="grid gap-4 md:grid-cols-2">
            <DiscountsMTDCard stats={stats} />
            <AddonMTDCard stats={stats} />
          </div>
          <AdsMTDCard stats={stats} />
        </>
      )}
    </div>
  );
}

// ───────── Hero (yesterday — respects outlet selector) ─────────

function HeroToday({
  stats,
  outletName,
}: {
  stats: Stats;
  outletName: string | null;
}) {
  return (
    <MotionCard delay={0.02}>
      <Card>
        <CardContent className="space-y-3 p-5 md:p-6">
          <div className="flex items-center gap-2 text-2xs uppercase tracking-wide text-muted-foreground">
            <Calendar className="h-3 w-3" />
            Yesterday · {outletName ?? 'all outlets'}
          </div>
          <div className="text-2xl font-semibold tabular-nums md:text-3xl">
            {formatINRCompact(stats.yest.netSales)}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DeltaChip
              label="vs same-day last week"
              delta={stats.yest.netSales - stats.sdlw.netSales}
              baseline={stats.sdlw.netSales}
              baselineDate={stats.sdlw.date}
            />
            <DeltaChip
              label="vs same-day last month"
              delta={stats.yest.netSales - stats.sdlm.netSales}
              baseline={stats.sdlm.netSales}
              baselineDate={stats.sdlm.date}
            />
            <span className="inline-flex items-center gap-1.5 rounded-md bg-violet-500/10 px-2.5 py-1.5 text-xs font-medium text-violet-700 dark:text-violet-400">
              Year proj{' '}
              <span className="tabular-nums">
                {formatINRCompact(stats.ytd.projected)}
              </span>
            </span>
          </div>
        </CardContent>
      </Card>
    </MotionCard>
  );
}

function DeltaChip({
  label,
  delta,
  baseline,
  baselineDate,
}: {
  label: string;
  delta: number;
  baseline: number;
  baselineDate: string;
}) {
  const up = delta > 0;
  const flat = Math.abs(delta) < 1;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const color = flat
    ? 'text-muted-foreground bg-muted'
    : up
      ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10'
      : 'text-rose-700 dark:text-rose-400 bg-rose-500/10';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium',
        color,
      )}
      title={`Baseline ${baselineDate}: ${formatINRCompact(baseline)}`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="tabular-nums">
        {up ? '+' : ''}
        {formatINRCompact(delta)}
      </span>
      <span className="font-normal opacity-80">{label}</span>
    </span>
  );
}

// ───────── MTD Pace ─────────

function MTDPaceCard({ stats }: { stats: Stats }) {
  const month = stats.today.toLocaleString('en-IN', { month: 'long' });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <CalendarClock className="h-4 w-4 text-violet-500" />
          {month} pace
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        <Row
          label="MTD net sales"
          value={formatINRCompact(stats.mtd.netSales)}
          sub={`day ${stats.mtd.daysElapsed} of ${stats.mtd.daysInMonth}`}
        />
        <Row
          label="Projected month-end"
          value={formatINRCompact(stats.mtd.projected)}
          sub="linear pace blended w/ same-period last month"
          emphasize
        />
        <Row
          label="Last month at same point"
          value={formatINRCompact(stats.lastMonthAtSamePoint)}
          sub={
            stats.lastMonthAtSamePoint > 0
              ? (() => {
                  const delta = stats.mtd.netSales - stats.lastMonthAtSamePoint;
                  const pct = (delta / stats.lastMonthAtSamePoint) * 100;
                  return `${delta >= 0 ? '+' : ''}${formatINRCompact(delta)} (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%) vs last-month MTD`;
                })()
              : 'no baseline yet'
          }
        />
      </CardContent>
    </Card>
  );
}

function QTDPaceCard({ stats }: { stats: Stats }) {
  const delta = stats.qtd.netSales - stats.lastQTDAtSamePoint;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <CalendarClock className="h-4 w-4 text-sky-500" />
          {stats.qtd.quarter} pace
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        <Row
          label="QTD net sales"
          value={formatINRCompact(stats.qtd.netSales)}
          sub={`day ${stats.qtd.daysElapsed} of ${stats.qtd.daysInQuarter}`}
        />
        <Row
          label="Projected quarter-end"
          value={formatINRCompact(stats.qtd.projected)}
          sub="linear pace"
          emphasize
        />
        <Row
          label="Last quarter same point"
          value={formatINRCompact(stats.lastQTDAtSamePoint)}
          sub={
            stats.lastQTDAtSamePoint > 0
              ? `${delta >= 0 ? '+' : ''}${formatINRCompact(delta)} vs same point`
              : 'no baseline yet'
          }
        />
        <Row
          label="Last quarter total"
          value={formatINRCompact(stats.lastQuarterTotal)}
          sub="for context"
        />
      </CardContent>
    </Card>
  );
}

function TargetsCard({ stats }: { stats: Stats }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Target className="h-4 w-4 text-emerald-500" />
          Targets — month, quarter, year
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-3">
        <TargetRow
          scope="month"
          period={stats.monthKey}
          actual={stats.mtd.netSales}
          projected={stats.mtd.projected}
          label={stats.today.toLocaleString('en-IN', { month: 'long' })}
        />
        <TargetRow
          scope="quarter"
          period={stats.quarterKey}
          actual={stats.qtd.netSales}
          projected={stats.qtd.projected}
          label={stats.quarterKey}
        />
        <TargetRow
          scope="year"
          period={stats.yearKey}
          actual={stats.ytd.netSales}
          projected={stats.ytd.projected}
          label={stats.yearKey}
        />
      </CardContent>
    </Card>
  );
}

function TargetRow({
  scope,
  period,
  actual,
  projected,
  label,
}: {
  scope: 'month' | 'quarter' | 'year';
  period: string;
  actual: number;
  projected: number;
  label: string;
}) {
  const qc = useQueryClient();
  const targetQ = useQuery({
    queryKey: ['sales-target', scope, period],
    queryFn: async (): Promise<number | null> => {
      const { data, error } = await marketing
        .from('sales_target')
        .select('target_inr')
        .eq('scope', scope)
        .eq('period', period)
        .eq('outlet_id', '')
        .maybeSingle();
      if (error) return null;
      return (data as { target_inr: number | null } | null)?.target_inr ?? null;
    },
    staleTime: 30_000,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const save = useMutation({
    mutationFn: async (value: number) => {
      const { error } = await marketing.from('sales_target').upsert(
        {
          scope,
          period,
          outlet_id: '',
          target_inr: value,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'scope,period,outlet_id' },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-target', scope, period] });
      setEditing(false);
    },
  });

  const target = targetQ.data;
  const pctOfTarget = target && target > 0 ? (actual / target) * 100 : null;
  const projVsTarget = target && target > 0 ? (projected / target) * 100 : null;
  const onTrack = projVsTarget != null && projVsTarget >= 100;

  return (
    <div className="rounded-md border border-border/40 p-3">
      <div className="flex items-center justify-between text-2xs uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        {!editing && (
          <button
            type="button"
            onClick={() => {
              setDraft(target ? String(target) : '');
              setEditing(true);
            }}
            className="inline-flex items-center gap-1 text-2xs hover:text-foreground"
          >
            <Pencil className="h-2.5 w-2.5" />
            {target ? 'edit' : 'set'}
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-2 flex items-center gap-1.5">
          <span className="text-2xs text-muted-foreground">₹</span>
          <input
            type="number"
            inputMode="numeric"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && Number(draft) > 0) save.mutate(Number(draft));
              if (e.key === 'Escape') setEditing(false);
            }}
            placeholder="e.g. 5000000"
            className="w-full rounded border border-border bg-background px-2 py-1 text-sm tabular-nums"
          />
          <button
            type="button"
            onClick={() => {
              if (Number(draft) > 0) save.mutate(Number(draft));
            }}
            disabled={save.isPending}
            className="rounded bg-primary p-1 text-primary-foreground"
          >
            {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          </button>
        </div>
      ) : (
        <>
          <div className="mt-1 text-lg font-semibold tabular-nums">
            {target ? formatINRCompact(target) : <span className="text-muted-foreground">—</span>}
          </div>
          {target ? (
            <>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    pctOfTarget != null && pctOfTarget >= 100
                      ? 'bg-emerald-500'
                      : onTrack
                        ? 'bg-emerald-500/70'
                        : pctOfTarget != null && pctOfTarget >= 50
                          ? 'bg-amber-500'
                          : 'bg-rose-500',
                  )}
                  style={{ width: `${Math.min(100, pctOfTarget ?? 0)}%` }}
                />
              </div>
              <div className="mt-1.5 flex items-center justify-between text-2xs">
                <span className="text-muted-foreground tabular-nums">
                  {(pctOfTarget ?? 0).toFixed(0)}% actual · proj{' '}
                  <span className={cn(onTrack ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
                    {(projVsTarget ?? 0).toFixed(0)}%
                  </span>
                </span>
              </div>
            </>
          ) : (
            <div className="mt-2 text-2xs text-muted-foreground">
              Click "set" to enter target.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DiscountsMTDCard({ stats }: { stats: Stats }) {
  const pct =
    stats.mtd.netSales > 0
      ? (stats.mtd.discountTotal / (stats.mtd.netSales + stats.mtd.discountTotal)) * 100
      : 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Wallet className="h-4 w-4 text-rose-500" />
          Discounts MTD
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        <Row
          label="Total discount given"
          value={formatINRCompact(stats.mtd.discountTotal)}
          sub={`${pct.toFixed(1)}% of gross MTD`}
          emphasize
        />
        <Row
          label="Implied gross MTD"
          value={formatINRCompact(stats.mtd.netSales + stats.mtd.discountTotal)}
          sub="net + discounts (before any aggregator-funded promo)"
        />
        <Row
          label="Avg discount per order"
          value={
            stats.mtd.orders > 0
              ? formatINRCompact(stats.mtd.discountTotal / stats.mtd.orders)
              : '—'
          }
          sub="across all orders this month"
        />
      </CardContent>
    </Card>
  );
}

function AddonMTDCard({ stats }: { stats: Stats }) {
  const attachPct =
    stats.mtd.netSales > 0 ? (stats.mtd.addonRevenue / stats.mtd.netSales) * 100 : 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Wallet className="h-4 w-4 text-emerald-500" />
          Add-on sale MTD
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        <Row
          label="Add-on revenue"
          value={formatINRCompact(stats.mtd.addonRevenue)}
          sub={`${attachPct.toFixed(1)}% of net sales`}
          emphasize
        />
        <Row
          label="Add-on attach rate"
          value={
            stats.mtd.orders > 0
              ? `${((stats.mtd.addonOrders / stats.mtd.orders) * 100).toFixed(1)}%`
              : '—'
          }
          sub={
            stats.mtd.addonOrders > 0
              ? `${formatNumberCompact(stats.mtd.addonOrders)} orders · ${formatINRCompact(stats.mtd.addonRevenue / stats.mtd.addonOrders)} / add-on order`
              : 'orders with an add-on'
          }
        />
        <Row
          label="Dine-in vs delivery"
          value={`${stats.mtd.dineinOrders} / ${stats.mtd.deliveryOrders}`}
          sub="MTD order counts"
        />
      </CardContent>
    </Card>
  );
}

function AdsMTDCard({ stats }: { stats: Stats }) {
  const [expanded, setExpanded] = useState(false);

  // Fetch per-(outlet, platform, date) ad spend + GMV so we can drill
  // by outlet × Swiggy/Zomato + compute ROAS-adjacent burn% per outlet.
  const spend = useQuery({
    queryKey: ['ads-mtd-detail', stats.monthKey],
    queryFn: async (): Promise<
      Array<{ outlet_id: string; platform: string; ads_spend: number; gmv: number }>
    > => {
      const monthStart = `${stats.monthKey}-01`;
      const { data, error } = await marketing
        .from('aggregator_daily')
        .select('outlet_id, platform, ads_spend, gmv')
        .gte('date', monthStart);
      if (error) return [];
      return (data ?? []) as unknown as Array<{
        outlet_id: string;
        platform: string;
        ads_spend: number;
        gmv: number;
      }>;
    },
    staleTime: 5 * 60_000,
  });

  // Aggregate: per (outlet, platform) and platform totals.
  const { byOutletPlatform, platformTotals, totalSpend } = useMemo(() => {
    const op = new Map<string, { ads_spend: number; gmv: number }>();
    const pt: Record<string, { ads_spend: number; gmv: number }> = {};
    let total = 0;
    for (const r of spend.data ?? []) {
      const key = `${r.outlet_id}::${r.platform}`;
      const cur = op.get(key) ?? { ads_spend: 0, gmv: 0 };
      cur.ads_spend += Number(r.ads_spend ?? 0);
      cur.gmv += Number(r.gmv ?? 0);
      op.set(key, cur);
      const ptEntry = pt[r.platform] ?? { ads_spend: 0, gmv: 0 };
      ptEntry.ads_spend += Number(r.ads_spend ?? 0);
      ptEntry.gmv += Number(r.gmv ?? 0);
      pt[r.platform] = ptEntry;
      total += Number(r.ads_spend ?? 0);
    }
    return { byOutletPlatform: op, platformTotals: pt, totalSpend: total };
  }, [spend.data]);

  const burnPct = stats.mtd.netSales > 0 ? (totalSpend / stats.mtd.netSales) * 100 : 0;
  // Total burn = ads + discount as a % of (net sales).
  const totalBurnPct =
    stats.mtd.netSales > 0
      ? ((totalSpend + stats.mtd.discountTotal) / stats.mtd.netSales) * 100
      : 0;

  const outletIds = useMemo(() => {
    const set = new Set<string>();
    for (const k of byOutletPlatform.keys()) set.add(k.split('::')[0]!);
    return [...set];
  }, [byOutletPlatform]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span className="inline-flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-amber-500" />
            Ad burn MTD
          </span>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-2xs text-muted-foreground hover:text-foreground"
          >
            <ChevronDown
              className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')}
            />
            {expanded ? 'Hide detail' : 'Show outlets × platform'}
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          <Stat label="Aggregator spend" value={formatINRCompact(totalSpend)} />
          <Stat
            label="Ad burn %"
            value={totalSpend > 0 ? `${burnPct.toFixed(1)}%` : '—'}
            tone={burnPct > 15 ? 'down' : burnPct > 8 ? undefined : 'up'}
          />
          <Stat
            label="Total burn % (ads + disc)"
            value={
              totalSpend + stats.mtd.discountTotal > 0 ? `${totalBurnPct.toFixed(1)}%` : '—'
            }
            tone={totalBurnPct > 25 ? 'down' : totalBurnPct > 15 ? undefined : 'up'}
          />
          <Stat label="Meta + Google" value="—" tone="muted" />
        </div>

        {expanded && (
          <div className="space-y-2 border-t border-border/40 pt-3">
            {totalSpend === 0 ? (
              <p className="text-2xs text-muted-foreground">
                No aggregator ad spend recorded MTD. Wires when Supermetrics
                writes to <code className="rounded bg-muted px-1">aggregator_daily.ads_spend</code>.
              </p>
            ) : (
              <>
                <PlatformTotalsRow totals={platformTotals} totalSpend={totalSpend} />
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[480px] text-xs">
                    <thead>
                      <tr className="border-b border-border/40 text-2xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-2 py-1.5 text-left">Outlet</th>
                        <th className="px-2 py-1.5 text-right">Swiggy ₹</th>
                        <th className="px-2 py-1.5 text-right">Swiggy %</th>
                        <th className="px-2 py-1.5 text-right">Zomato ₹</th>
                        <th className="px-2 py-1.5 text-right">Zomato %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outletIds
                        .map((oid) => {
                          const sw = byOutletPlatform.get(`${oid}::swiggy`) ?? {
                            ads_spend: 0,
                            gmv: 0,
                          };
                          const zo = byOutletPlatform.get(`${oid}::zomato`) ?? {
                            ads_spend: 0,
                            gmv: 0,
                          };
                          // Per-outlet sales for burn %.
                          const out = stats.perOutlet.find((p) => p.outletId === oid);
                          const outletNet = out?.netSalesMtd ?? 0;
                          return { oid, sw, zo, outletNet };
                        })
                        .sort(
                          (a, b) =>
                            b.sw.ads_spend + b.zo.ads_spend - (a.sw.ads_spend + a.zo.ads_spend),
                        )
                        .map(({ oid, sw, zo, outletNet }) => {
                          const swPct =
                            outletNet > 0 ? (sw.ads_spend / outletNet) * 100 : 0;
                          const zoPct =
                            outletNet > 0 ? (zo.ads_spend / outletNet) * 100 : 0;
                          return (
                            <tr key={oid} className="border-b border-border/20 last:border-0">
                              <td className="px-2 py-1.5 font-medium capitalize">
                                {oid.replace(/-/g, ' ')}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums">
                                {formatINRCompact(sw.ads_spend)}
                              </td>
                              <td
                                className={cn(
                                  'px-2 py-1.5 text-right tabular-nums',
                                  swPct > 15 && 'text-rose-600 dark:text-rose-400',
                                )}
                              >
                                {sw.ads_spend > 0 ? `${swPct.toFixed(1)}%` : '—'}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums">
                                {formatINRCompact(zo.ads_spend)}
                              </td>
                              <td
                                className={cn(
                                  'px-2 py-1.5 text-right tabular-nums',
                                  zoPct > 15 && 'text-rose-600 dark:text-rose-400',
                                )}
                              >
                                {zo.ads_spend > 0 ? `${zoPct.toFixed(1)}%` : '—'}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PlatformTotalsRow({
  totals,
  totalSpend,
}: {
  totals: Record<string, { ads_spend: number; gmv: number }>;
  totalSpend: number;
}) {
  const platforms = Object.keys(totals).sort();
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${platforms.length}, minmax(0, 1fr))` }}>
      {platforms.map((p) => {
        const t = totals[p] ?? { ads_spend: 0, gmv: 0 };
        const share = totalSpend > 0 ? (t.ads_spend / totalSpend) * 100 : 0;
        return (
          <div key={p} className="rounded-md border border-border/40 px-2 py-1.5 text-2xs">
            <div className="capitalize text-muted-foreground">{p}</div>
            <div className="mt-0.5 flex items-baseline justify-between">
              <span className="font-semibold tabular-nums">
                {formatINRCompact(t.ads_spend)}
              </span>
              <span className="text-2xs text-muted-foreground">
                {share.toFixed(0)}% of spend
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ───────── Outlet matrix (replaces narrow per-outlet growth list) ─────────

type MatrixSortKey = 'mtd' | 'yest' | 'aov' | 'discount' | 'ads' | 'name';

function PerOutletGrowth({
  stats,
  outlets,
}: {
  stats: Stats;
  outlets: { id: string; name: string; city: string | null }[];
}) {
  const [sortKey, setSortKey] = useState<MatrixSortKey>('mtd');
  const [sortDesc, setSortDesc] = useState(true);

  // Per-outlet aggregator ad spend MTD (sums Swiggy + Zomato).
  const adsByOutlet = useQuery({
    queryKey: ['outlet-ads-mtd', stats.monthKey],
    queryFn: async (): Promise<Map<string, number>> => {
      const monthStart = `${stats.monthKey}-01`;
      const { data, error } = await marketing
        .from('aggregator_daily')
        .select('outlet_id, ads_spend')
        .gte('date', monthStart);
      if (error) return new Map();
      const m = new Map<string, number>();
      for (const r of (data ?? []) as Array<{ outlet_id: string; ads_spend: number | null }>) {
        m.set(r.outlet_id, (m.get(r.outlet_id) ?? 0) + Number(r.ads_spend ?? 0));
      }
      return m;
    },
    staleTime: 5 * 60_000,
  });

  const adsMap = useMemo(
    () => adsByOutlet.data ?? new Map<string, number>(),
    [adsByOutlet.data],
  );

  // Augmented rows with all the metrics we need + sort key extraction.
  const enriched = useMemo(() => {
    return stats.perOutlet.map((r) => {
      const adsMtd = adsMap.get(r.outletId) ?? 0;
      const discPct =
        r.netSalesMtd + r.discountMtd > 0
          ? (r.discountMtd / (r.netSalesMtd + r.discountMtd)) * 100
          : 0;
      const adsPct = r.netSalesMtd > 0 ? (adsMtd / r.netSalesMtd) * 100 : 0;
      const totalBurnPct =
        r.netSalesMtd > 0 ? ((adsMtd + r.discountMtd) / r.netSalesMtd) * 100 : 0;
      const outlet = outlets.find((o) => o.id === r.outletId);
      return {
        ...r,
        name: outlet?.name ?? r.outletId,
        city: outlet?.city ?? null,
        adsMtd,
        discPct,
        adsPct,
        totalBurnPct,
      };
    });
  }, [stats.perOutlet, outlets, adsMap]);

  const sorted = useMemo(() => {
    const arr = [...enriched];
    arr.sort((a, b) => {
      const dir = sortDesc ? -1 : 1;
      switch (sortKey) {
        case 'mtd':
          return (a.netSalesMtd - b.netSalesMtd) * dir;
        case 'yest':
          return (a.netSalesYest - b.netSalesYest) * dir;
        case 'aov':
          return (a.aovMtd - b.aovMtd) * dir;
        case 'discount':
          return (a.discPct - b.discPct) * dir;
        case 'ads':
          return (a.adsPct - b.adsPct) * dir;
        case 'name':
          return a.name.localeCompare(b.name) * dir;
      }
    });
    return arr;
  }, [enriched, sortKey, sortDesc]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Outlet matrix · MTD</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {sorted.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            No outlets in scope.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-border/40 text-2xs uppercase tracking-wide text-muted-foreground">
                  <SortHead onClick={() => toggle('name', sortKey, sortDesc, setSortKey, setSortDesc)} active={sortKey === 'name'} desc={sortDesc} align="left">Outlet</SortHead>
                  <SortHead onClick={() => toggle('yest', sortKey, sortDesc, setSortKey, setSortDesc)} active={sortKey === 'yest'} desc={sortDesc} align="right">Yesterday</SortHead>
                  <SortHead onClick={() => toggle('mtd', sortKey, sortDesc, setSortKey, setSortDesc)} active={sortKey === 'mtd'} desc={sortDesc} align="right">MTD net</SortHead>
                  <SortHead onClick={() => toggle('aov', sortKey, sortDesc, setSortKey, setSortDesc)} active={sortKey === 'aov'} desc={sortDesc} align="right">AOV</SortHead>
                  <SortHead onClick={() => toggle('discount', sortKey, sortDesc, setSortKey, setSortDesc)} active={sortKey === 'discount'} desc={sortDesc} align="right">Disc %</SortHead>
                  <SortHead onClick={() => toggle('ads', sortKey, sortDesc, setSortKey, setSortDesc)} active={sortKey === 'ads'} desc={sortDesc} align="right">Ads % (₹)</SortHead>
                  <th className="px-3 py-2 text-right">Total burn</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.outletId} className="border-b border-border/30 last:border-0 hover:bg-accent/20">
                    <td className="px-4 py-3">
                      <div className="font-medium leading-tight">{r.name}</div>
                      {r.city && <div className="text-2xs text-muted-foreground">{r.city}</div>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="text-base font-semibold tabular-nums">
                        {formatINRCompact(r.netSalesYest)}
                      </div>
                      <div className={cn('text-2xs tabular-nums', r.deltaVsSdlw > 0 ? 'text-emerald-600 dark:text-emerald-400' : r.deltaVsSdlw < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground')}>
                        {r.deltaVsSdlw >= 0 ? '+' : ''}{formatINRCompact(r.deltaVsSdlw)} vs SDLW
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="text-base font-semibold tabular-nums">
                        {formatINRCompact(r.netSalesMtd)}
                      </div>
                      <div className="text-2xs text-muted-foreground tabular-nums">
                        {r.ordersMtd} orders
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right text-base font-semibold tabular-nums">
                      {r.aovMtd > 0 ? formatINRCompact(r.aovMtd) : '—'}
                    </td>
                    <td className={cn('px-3 py-3 text-right text-base font-semibold tabular-nums', r.discPct > 15 ? 'text-rose-600 dark:text-rose-400' : r.discPct > 8 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground')}>
                      {r.discPct.toFixed(1)}%
                      <div className="text-2xs font-normal text-muted-foreground tabular-nums">
                        {formatINRCompact(r.discountMtd)}
                      </div>
                    </td>
                    <td className={cn('px-3 py-3 text-right text-base font-semibold tabular-nums', r.adsPct > 15 ? 'text-rose-600 dark:text-rose-400' : r.adsPct > 8 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground')}>
                      {r.adsMtd > 0 ? `${r.adsPct.toFixed(1)}%` : '—'}
                      <div className="text-2xs font-normal text-muted-foreground tabular-nums">
                        {r.adsMtd > 0 ? formatINRCompact(r.adsMtd) : '—'}
                      </div>
                    </td>
                    <td className={cn('px-3 py-3 text-right text-base font-semibold tabular-nums', r.totalBurnPct > 25 ? 'text-rose-600 dark:text-rose-400' : r.totalBurnPct > 15 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400')}>
                      {r.totalBurnPct.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SortHead({
  children,
  active,
  desc,
  onClick,
  align,
}: {
  children: React.ReactNode;
  active: boolean;
  desc: boolean;
  onClick: () => void;
  align: 'left' | 'right';
}) {
  return (
    <th className={cn('px-3 py-2', align === 'right' ? 'text-right' : 'text-left')}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1 text-2xs uppercase tracking-wide',
          active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        {children}
        {active && (desc ? <ArrowDownRight className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />)}
      </button>
    </th>
  );
}

function toggle(
  next: MatrixSortKey,
  current: MatrixSortKey,
  currentDesc: boolean,
  setKey: (k: MatrixSortKey) => void,
  setDesc: (d: boolean) => void,
) {
  if (next === current) setDesc(!currentDesc);
  else {
    setKey(next);
    setDesc(true);
  }
}

// ───────── MTD headline tile — respects the global outlet selector ─────────

function FlaxMTDAllCities({
  stats,
  outletName,
}: {
  stats: Stats;
  /** Name of the selected outlet, or null for pan-Flax. */
  outletName: string | null;
}) {
  // When an outlet is selected, show that outlet's scoped numbers; otherwise
  // show the pan-Flax all-cities roll-up.
  const isScoped = outletName !== null;
  const mtdNet = isScoped ? stats.mtd.netSales : stats.flaxAll.mtdNet;
  const lmAtSamePoint = isScoped ? stats.lastMonthAtSamePoint : stats.flaxAll.lmAtSamePoint;
  const lyAtSamePoint = isScoped ? stats.scopedLyAtSamePoint : stats.flaxAll.lyAtSamePoint;
  const lyFullMonth = isScoped ? stats.scopedLyFullMonth : stats.flaxAll.lyFullMonth;
  const lmDelta = mtdNet - lmAtSamePoint;
  const lmPct = lmAtSamePoint > 0 ? (lmDelta / lmAtSamePoint) * 100 : null;
  const lyDelta = mtdNet - lyAtSamePoint;
  const lyPct = lyAtSamePoint > 0 ? (lyDelta / lyAtSamePoint) * 100 : null;
  const month = stats.today.toLocaleString('en-IN', { month: 'long' });

  return (
    <MotionCard delay={0.03}>
      <Card className="border-violet-500/20 bg-gradient-to-br from-violet-500/[0.03] to-transparent">
        <CardContent className="space-y-3 p-5 md:p-6">
          <div className="flex items-center gap-2 text-2xs uppercase tracking-wide text-muted-foreground">
            <Building2 className="h-3 w-3 text-violet-500" />
            {isScoped ? `MTD ${outletName} · ${month}` : `MTD Flax sales · all cities · ${month}`}
          </div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-4xl font-semibold tabular-nums md:text-5xl">
              {formatINRCompact(mtdNet)}
            </span>
            <span className="text-2xs text-muted-foreground">
              day {stats.mtd.daysElapsed} of {stats.mtd.daysInMonth}
            </span>
            {!isScoped && stats.outletCounts.mtd > 0 && (
              <OutletCountChip
                count={stats.outletCounts.mtd}
                label={`${month} ${stats.today.getFullYear()}`}
                tone="current"
              />
            )}
            {!isScoped && stats.outletCounts.ly > 0 && (
              <OutletCountChip
                count={stats.outletCounts.ly}
                label={`${month} ${stats.today.getFullYear() - 1}`}
                tone="ly"
              />
            )}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <ComparisonChip
              label="vs last month MTD"
              sub={
                lmAtSamePoint > 0
                  ? `${month === 'January' ? 'Dec' : 'prev month'} day 1-${stats.mtd.daysElapsed}`
                  : 'no baseline'
              }
              delta={lmDelta}
              pct={lmPct}
              baseline={lmAtSamePoint}
            />
            <ComparisonChip
              label={`vs ${month} ${stats.today.getFullYear() - 1}`}
              sub={
                lyAtSamePoint > 0
                  ? `same period last year · full month ${formatINRCompact(lyFullMonth)}`
                  : 'no data for that year yet (backfill covers Jun 2024 → May 2025)'
              }
              delta={lyDelta}
              pct={lyPct}
              baseline={lyAtSamePoint}
            />
          </div>
        </CardContent>
      </Card>
    </MotionCard>
  );
}

function OutletCountChip({
  count,
  label,
  tone,
}: {
  count: number;
  label: string;
  tone: 'current' | 'ly';
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium tabular-nums',
        tone === 'current'
          ? 'bg-violet-500/10 text-violet-700 dark:text-violet-300'
          : 'bg-muted/60 text-muted-foreground',
      )}
      title={`${count} outlets recorded sales in ${label}`}
    >
      <Building2 className="h-3 w-3" />
      {count} outlets · {label}
    </span>
  );
}

function ComparisonChip({
  label,
  sub,
  delta,
  pct,
  baseline,
}: {
  label: string;
  sub: string;
  delta: number;
  pct: number | null;
  baseline: number;
}) {
  if (baseline <= 0) {
    return (
      <div className="rounded-md border border-dashed border-border/40 px-3 py-2.5">
        <div className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-1 text-sm text-muted-foreground">{sub}</div>
      </div>
    );
  }
  const up = delta > 0;
  const flat = Math.abs(delta) < 1;
  const color = flat
    ? 'border-border bg-card text-muted-foreground'
    : up
      ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400'
      : 'border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-400';
  // Lead with the baseline rupee value (the "what we did then" number) since
  // that's what the operator wants to read first. Delta % + ₹ shifts to a
  // smaller line below — still colour-coded so direction stays glanceable.
  const baselineLabel = formatINRCompact(baseline);
  return (
    <div className={cn('rounded-md border px-3 py-2.5', color)}>
      <div className="text-2xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums leading-tight md:text-3xl">
        {baselineLabel}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5 text-xs font-medium tabular-nums">
        <span>
          {pct != null ? `${up ? '+' : ''}${pct.toFixed(1)}%` : '—'}
        </span>
        <span className="opacity-70">
          · {up ? '+' : ''}{formatINRCompact(delta)}
        </span>
      </div>
      <div className="mt-1 text-2xs opacity-70">{sub}</div>
    </div>
  );
}

// ───────── City cards (with click-to-drill outlet detail) ─────────

interface OutletForCity {
  id: string;
  name: string;
  city: string | null;
}

function CityCards({
  stats,
  outlets,
}: {
  stats: Stats;
  outlets: OutletForCity[];
}) {
  // Bucket outlets into city groups. Mumbai includes everything in
  // Greater Mumbai (Bombay-area suburbs). Bangalore = Bengaluru.
  // Anything else falls under 'Other'.
  const groups = useMemo(() => {
    const map = new Map<string, { city: string; outletIds: string[] }>();
    for (const o of outlets) {
      const city = normalizeCity(o.city);
      const g = map.get(city) ?? { city, outletIds: [] };
      g.outletIds.push(o.id);
      map.set(city, g);
    }
    // Ensure Mumbai / Bangalore / Goa always appear in this order even
    // if empty, so the founder sees the full geographic picture.
    const ordered = ['Mumbai', 'Bangalore', 'Goa'];
    const out: Array<{ city: string; outletIds: string[] }> = [];
    for (const c of ordered) out.push(map.get(c) ?? { city: c, outletIds: [] });
    for (const [city, g] of map) {
      if (!ordered.includes(city)) out.push(g);
    }
    return out;
  }, [outlets]);

  const totalMtd = stats.mtd.netSales || 1; // avoid /0

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {groups.map((g) => (
        <CityCard
          key={g.city}
          city={g.city}
          outletIds={g.outletIds}
          outlets={outlets}
          stats={stats}
          shareOfTotal={
            sumMtd(stats, g.outletIds) > 0 ? sumMtd(stats, g.outletIds) / totalMtd : 0
          }
        />
      ))}
    </div>
  );
}

function CityCard({
  city,
  outletIds,
  outlets,
  stats,
  shareOfTotal,
}: {
  city: string;
  outletIds: string[];
  outlets: OutletForCity[];
  stats: Stats;
  shareOfTotal: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const cityRows = stats.perOutlet.filter((r) => outletIds.includes(r.outletId));
  const mtdNet = cityRows.reduce((s, r) => s + r.netSalesMtd, 0);
  const mtdOrders = cityRows.reduce((s, r) => s + r.ordersMtd, 0);
  const mtdDiscount = cityRows.reduce((s, r) => s + r.discountMtd, 0);
  const mtdAddon = cityRows.reduce((s, r) => s + r.addonMtd, 0);
  const yestNet = cityRows.reduce((s, r) => s + r.netSalesYest, 0);
  const sdlwNet = cityRows.reduce((s, r) => s + r.netSalesSdlw, 0);
  const yestDelta = yestNet - sdlwNet;
  const cityAov = mtdOrders > 0 ? mtdNet / mtdOrders : 0;
  const discountPct = mtdNet + mtdDiscount > 0 ? (mtdDiscount / (mtdNet + mtdDiscount)) * 100 : 0;
  const addonPct = mtdNet > 0 ? (mtdAddon / mtdNet) * 100 : 0;
  const lmNet = cityRows.reduce((s, r) => s + r.netLmAtSamePoint, 0);
  const vsLmDelta = mtdNet - lmNet;
  const vsLmPct = lmNet > 0 ? (vsLmDelta / lmNet) * 100 : 0;

  const isEmpty = outletIds.length === 0;

  return (
    <Card
      className={cn(
        'transition-colors',
        isEmpty ? 'border-dashed opacity-70' : expanded && 'border-primary/40',
      )}
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4 text-violet-500" />
            {city}
          </span>
          <span className="text-2xs font-normal text-muted-foreground">
            {outletIds.length} outlet{outletIds.length === 1 ? '' : 's'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isEmpty ? (
          <p className="text-2xs text-muted-foreground">No outlets yet.</p>
        ) : (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-semibold tabular-nums">
                {formatINRCompact(mtdNet)}
              </span>
              <span className="text-2xs text-muted-foreground">
                {(shareOfTotal * 100).toFixed(0)}% of total
              </span>
            </div>
            {lmNet > 0 && (
              <div
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-2xs font-medium',
                  Math.abs(vsLmPct) < 1
                    ? 'bg-muted text-muted-foreground'
                    : vsLmPct > 0
                      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                      : 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
                )}
                title={`Same period (day 1-${stats.mtd.daysElapsed}) of last month: ${formatINRCompact(lmNet)}`}
              >
                {vsLmPct > 0 ? '+' : ''}
                {vsLmPct.toFixed(1)}% vs last-month MTD
                <span className="opacity-70 tabular-nums">
                  ({vsLmDelta > 0 ? '+' : ''}
                  {formatINRCompact(vsLmDelta)})
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-2xs">
              <Stat label="Avg AOV" value={cityAov > 0 ? formatINRCompact(cityAov) : '—'} />
              <Stat
                label="Yest vs SDLW"
                value={`${yestDelta >= 0 ? '+' : ''}${formatINRCompact(yestDelta)}`}
                tone={Math.abs(yestDelta) < 1 ? 'muted' : yestDelta > 0 ? 'up' : 'down'}
              />
              <Stat label="Discount %" value={`${discountPct.toFixed(1)}%`} />
              <Stat
                label="Add-on % of net"
                value={mtdAddon > 0 ? `${addonPct.toFixed(1)}% (${formatINRCompact(mtdAddon)})` : '—'}
              />
            </div>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 inline-flex w-full items-center justify-center gap-1 rounded-md border border-border/40 px-2 py-1.5 text-2xs text-muted-foreground hover:bg-accent"
            >
              <ChevronDown
                className={cn(
                  'h-3 w-3 transition-transform',
                  expanded && 'rotate-180',
                )}
              />
              {expanded ? 'Hide' : 'Show'} outlets
            </button>

            {expanded && (
              <div className="mt-2 space-y-1.5 border-t border-border/40 pt-2">
                {cityRows
                  .sort((a, b) => b.netSalesMtd - a.netSalesMtd)
                  .map((r) => {
                    const o = outlets.find((x) => x.id === r.outletId);
                    const pct =
                      r.netSalesMtd + r.discountMtd > 0
                        ? (r.discountMtd / (r.netSalesMtd + r.discountMtd)) * 100
                        : 0;
                    return (
                      <div key={r.outletId} className="rounded-md bg-muted/30 p-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium">{o?.name ?? r.outletId}</span>
                          <span className="tabular-nums font-semibold">
                            {formatINRCompact(r.netSalesMtd)}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-2xs text-muted-foreground">
                          <span className="tabular-nums">
                            AOV {r.aovMtd > 0 ? formatINRCompact(r.aovMtd) : '—'}
                          </span>
                          <span className="tabular-nums">Disc {pct.toFixed(1)}%</span>
                          <span className="tabular-nums">
                            Yest {formatINRCompact(r.netSalesYest)}
                          </span>
                          <span
                            className={cn(
                              'tabular-nums',
                              Math.abs(r.deltaVsSdlw) < 1
                                ? 'text-muted-foreground'
                                : r.deltaVsSdlw > 0
                                  ? 'text-emerald-600 dark:text-emerald-400'
                                  : 'text-rose-600 dark:text-rose-400',
                            )}
                          >
                            {r.deltaVsSdlw >= 0 ? '+' : ''}
                            {formatINRCompact(r.deltaVsSdlw)} vs SDLW
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'up' | 'down' | 'muted';
}) {
  return (
    <div className="rounded-md border border-border/40 px-2 py-1.5">
      <div className="text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-0.5 text-sm font-semibold tabular-nums',
          tone === 'up' && 'text-emerald-700 dark:text-emerald-400',
          tone === 'down' && 'text-rose-700 dark:text-rose-400',
          tone === 'muted' && 'text-muted-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function normalizeCity(raw: string | null): string {
  if (!raw) return 'Other';
  const c = raw.trim().toLowerCase();
  if (c === 'mumbai' || c === 'bombay' || c.includes('mumbai')) return 'Mumbai';
  if (c === 'bangalore' || c === 'bengaluru' || c.includes('bangalor')) return 'Bangalore';
  if (c === 'goa' || c.includes('goa')) return 'Goa';
  return raw;
}

function sumMtd(stats: Stats, outletIds: string[]): number {
  return stats.perOutlet
    .filter((r) => outletIds.includes(r.outletId))
    .reduce((s, r) => s + r.netSalesMtd, 0);
}

// ───────── Placeholders (data not in schema yet) ─────────


// ───────── SyncBar ─────────

function FreshnessBar({
  lastSyncedAt,
  liveSyncedAt,
  syncing,
}: {
  lastSyncedAt: string | null;
  liveSyncedAt: string | null;
  syncing: boolean;
}) {
  // History (date < today) comes from petpooja_orders, written by the
  // inventory pipeline at ~07:00 IST. Today's running comes from
  // outlet_sales_live, refreshed via sync-on-load + a 30-min cron.
  const stale =
    !lastSyncedAt || Date.now() - new Date(lastSyncedAt).getTime() > 12 * 3600 * 1000;
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border px-3 py-2 text-2xs',
        syncing
          ? 'border-sky-500/40 bg-sky-500/5'
          : stale
            ? 'border-amber-500/40 bg-amber-500/5'
            : 'border-border/40 bg-card',
      )}
    >
      {syncing ? (
        <Loader2 className="h-3 w-3 animate-spin text-sky-500" />
      ) : (
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-full',
            stale ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse',
          )}
        />
      )}
      <span className="text-muted-foreground">
        {syncing ? (
          <>Refreshing today's numbers from Petpooja…</>
        ) : lastSyncedAt ? (
          <>
            History synced {relativeTime(lastSyncedAt)}
            {liveSyncedAt ? <> · today {relativeTime(liveSyncedAt)}</> : null}
          </>
        ) : (
          <>No sync history yet</>
        )}
      </span>
    </div>
  );
}

// ───────── Shared atoms ─────────

function Row({
  label,
  value,
  sub,
  emphasize,
}: {
  label: string;
  value: string;
  sub: string;
  emphasize?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-2xs text-muted-foreground">{label}</div>
        <div className="text-2xs text-muted-foreground/70">{sub}</div>
      </div>
      <span
        className={cn(
          'shrink-0 tabular-nums',
          emphasize ? 'text-lg font-semibold' : 'text-base font-medium',
        )}
      >
        {value}
      </span>
    </div>
  );
}

// ───────── Stats computation ─────────

interface Stats {
  today: Date;
  yest: { date: string; netSales: number };
  sdlw: { date: string; netSales: number }; // same-day last week
  sdlm: { date: string; netSales: number }; // same-day last month
  mtd: {
    netSales: number;
    orders: number;
    covers: number;
    discountTotal: number;
    addonRevenue: number;
    addonOrders: number;
    dineinOrders: number;
    deliveryOrders: number;
    daysElapsed: number;
    daysInMonth: number;
    projected: number;
  };
  qtd: {
    netSales: number;
    quarter: string; // '2026-Q2'
    daysElapsed: number;
    daysInQuarter: number;
    projected: number;
  };
  lastQTDAtSamePoint: number;
  lastQuarterTotal: number;
  ytd: {
    netSales: number;
    year: number;
    daysElapsed: number;
    daysInYear: number;
    projected: number;
  };
  monthKey: string; // '2026-05'
  yearKey: string; // '2026'
  quarterKey: string;
  lastMonthAtSamePoint: number;
  // Same-period-last-year, scoped to the current outlet selection.
  // Pairs with mtd.netSales for the headline tile's LY chip.
  scopedLyAtSamePoint: number;
  scopedLyFullMonth: number;
  // Distinct outlet counts per period — exposed so the headline tile can
  // chip them next to the comparison values (Flax grew from N → M outlets
  // between Dec 2024 and Dec 2025, the LY comparison isn't apples-to-apples).
  outletCounts: {
    mtd: number;
    lm: number;
    ly: number;
  };
  // All-Flax (every outlet, ignoring global selector) for the
  // "MTD Flax Sales — all cities" tile when no outlet is selected.
  flaxAll: {
    mtdNet: number;
    lmAtSamePoint: number;
    lyAtSamePoint: number;
    lyFullMonth: number;
  };
  perOutlet: Array<{
    outletId: string;
    netSalesYest: number;
    netSalesSdlw: number;
    deltaVsSdlw: number;
    ordersMtd: number;
    netSalesMtd: number;
    aovMtd: number;
    discountMtd: number;
    addonMtd: number;
    netLmAtSamePoint: number;
  }>;
}

function computeStats(
  rows: DailyRow[],
  today: Date,
  outlets: { id: string }[],
  outletsAll: { id: string }[],
): Stats {
  const yest = isoDate(addDays(today, -1));
  const sdlw = isoDate(addDays(today, -8));
  const sdlm = isoDate(sameDayLastMonth(addDays(today, -1)));

  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const daysElapsed = today.getDate(); // today's day-of-month, e.g. 31 on May 31
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

  const byOutletDate = new Map<string, DailyRow>();
  for (const r of rows) byOutletDate.set(`${r.outlet_id}::${r.date}`, r);

  const get = (outletId: string, date: string) => byOutletDate.get(`${outletId}::${date}`);
  // "Net sales" = item revenue after discounts (core − discount). The daily feed
  // stores net_sales as the pre-discount item value, so subtract discount_total.
  const netOf = (r: DailyRow | undefined) =>
    Number(r?.net_sales ?? 0) - Number(r?.discount_total ?? 0);
  const sumFor = (date: string) => outlets.reduce((s, o) => s + netOf(get(o.id, date)), 0);

  const yestSum = sumFor(yest);
  const sdlwSum = sumFor(sdlw);
  const sdlmSum = sumFor(sdlm);

  // All aggregations narrow to the outlets in scope. When the global
  // selector is on 'all', `outlets` is every outlet; when a specific
  // outlet is picked, `outlets` is [that one] and the dashboard math
  // honours that scope across MTD / QTD / YTD / lastMonth.
  const scopedOutletIds = new Set(outlets.map((o) => o.id));
  const inScope = (r: DailyRow) => scopedOutletIds.has(r.outlet_id);

  // Anchor date in 1-indexed components from the user-local Date.
  // We compare against r.date (YYYY-MM-DD string) parsed integer-wise
  // — never construct Date(r.date) again, since "YYYY-MM-DD" parses as
  // UTC midnight which trips on timezone boundaries.
  const todayY = today.getFullYear();
  const todayM = today.getMonth() + 1; // 1-indexed
  const lmM = lastMonthStart.getMonth() + 1;
  const lmY = lastMonthStart.getFullYear();
  const lyY = todayY - 1;
  const lyM = todayM;

  function parseRowDate(s: string): { y: number; m: number; d: number } {
    return {
      y: Number(s.slice(0, 4)),
      m: Number(s.slice(5, 7)),
      d: Number(s.slice(8, 10)),
    };
  }

  // MTD aggregation across scoped outlets.
  let mtdNet = 0;
  let mtdOrders = 0;
  let mtdCovers = 0;
  let mtdDiscount = 0;
  let mtdAddon = 0;
  let mtdAddonOrders = 0;
  let mtdDinein = 0;
  let mtdDelivery = 0;
  for (const r of rows) {
    if (!inScope(r)) continue;
    const pd = parseRowDate(r.date);
    if (pd.y === todayY && pd.m === todayM && pd.d <= daysElapsed) {
      mtdNet += netOf(r);
      mtdOrders += Number(r.orders ?? 0);
      mtdCovers += Number(r.covers ?? 0);
      mtdDiscount += Number(r.discount_total ?? 0);
      mtdAddon += Number(r.addon_revenue ?? 0);
      mtdAddonOrders += Number(r.addon_orders ?? 0);
      mtdDinein += Number(r.dinein_orders ?? 0);
      mtdDelivery += Number(r.delivery_orders ?? 0);
    }
  }
  // Last-month-at-same-point: sum of last month's days 1..daysElapsed.
  let lastMonthAtSamePoint = 0;
  for (const r of rows) {
    if (!inScope(r)) continue;
    const pd = parseRowDate(r.date);
    if (pd.y === lmY && pd.m === lmM && pd.d <= daysElapsed) {
      lastMonthAtSamePoint += netOf(r);
    }
  }

  // Scoped last-year — same month, prior year.
  let scopedLyAtSamePoint = 0;
  let scopedLyFullMonth = 0;
  for (const r of rows) {
    if (!inScope(r)) continue;
    const pd = parseRowDate(r.date);
    if (pd.y === lyY && pd.m === lyM) {
      scopedLyFullMonth += netOf(r);
      if (pd.d <= daysElapsed) scopedLyAtSamePoint += netOf(r);
    }
  }

  // Projection: blend linear pace w/ last-month run-rate. Weight 50/50.
  const linearProj = daysElapsed > 0 ? (mtdNet / daysElapsed) * daysInMonth : 0;
  const lastMonthRate =
    lastMonthAtSamePoint > 0 ? (lastMonthAtSamePoint / daysElapsed) * daysInMonth : 0;
  const projected =
    lastMonthRate > 0 ? Math.round((linearProj + lastMonthRate) / 2) : Math.round(linearProj);

  // Quarter & year math.
  const quarterIdx = Math.floor(today.getMonth() / 3); // 0..3
  const quarterStart = new Date(today.getFullYear(), quarterIdx * 3, 1);
  const quarterEnd = new Date(today.getFullYear(), quarterIdx * 3 + 3, 0);
  const daysInQuarter = Math.floor(
    (quarterEnd.getTime() - quarterStart.getTime()) / 86400000,
  ) + 1;
  const daysIntoQuarter = Math.floor(
    (today.getTime() - quarterStart.getTime()) / 86400000,
  ) + 1;
  const lastQuarterStart = new Date(today.getFullYear(), (quarterIdx - 1) * 3, 1);
  const lastQuarterEnd = new Date(today.getFullYear(), quarterIdx * 3, 0);
  const yearStart = new Date(today.getFullYear(), 0, 1);
  const yearEnd = new Date(today.getFullYear(), 11, 31);
  const daysInYear = Math.floor((yearEnd.getTime() - yearStart.getTime()) / 86400000) + 1;
  const daysIntoYear = Math.floor((today.getTime() - yearStart.getTime()) / 86400000) + 1;

  // QTD / YTD — string-based date comparison, same anti-TZ pattern as MTD.
  // Quarter / year boundaries become year + month + day-of-year integers.
  const qStartY = quarterStart.getFullYear();
  const qStartM = quarterStart.getMonth() + 1;
  const lqStartY = lastQuarterStart.getFullYear();
  const lqStartM = lastQuarterStart.getMonth() + 1;
  const lqEndY = lastQuarterEnd.getFullYear();
  const lqEndM = lastQuarterEnd.getMonth() + 1;
  const lqEndD = lastQuarterEnd.getDate();
  // Compare YYYYMMDD as a sortable integer.
  const ymdInt = (y: number, m: number, d: number) => y * 10000 + m * 100 + d;
  const qStartInt = ymdInt(qStartY, qStartM, 1);
  const lqStartInt = ymdInt(lqStartY, lqStartM, 1);
  const lqEndInt = ymdInt(lqEndY, lqEndM, lqEndD);
  const todayInt = ymdInt(todayY, todayM, daysElapsed);
  const yearStartInt = ymdInt(todayY, 1, 1);

  let qtdNet = 0;
  let lastQTDAtSamePoint = 0;
  let lastQuarterTotal = 0;
  let ytdNet = 0;
  for (const r of rows) {
    const pd = parseRowDate(r.date);
    const pdInt = ymdInt(pd.y, pd.m, pd.d);
    const v = netOf(r);
    if (pdInt >= qStartInt && pdInt <= todayInt) qtdNet += v;
    if (pdInt >= yearStartInt && pdInt <= todayInt) ytdNet += v;
    if (pdInt >= lqStartInt && pdInt <= lqEndInt) {
      lastQuarterTotal += v;
      // Same-position-in-last-quarter: day-offset from lastQuarterStart.
      const offsetDays =
        (Date.UTC(pd.y, pd.m - 1, pd.d) - Date.UTC(lqStartY, lqStartM - 1, 1)) / 86400000;
      if (offsetDays < daysIntoQuarter) lastQTDAtSamePoint += v;
    }
  }
  const qtdProjected = daysIntoQuarter > 0
    ? Math.round((qtdNet / daysIntoQuarter) * daysInQuarter)
    : 0;
  const ytdProjected = daysIntoYear > 0
    ? Math.round((ytdNet / daysIntoYear) * daysInYear)
    : 0;

  const monthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const yearKey = String(today.getFullYear());
  const quarterKey = `${today.getFullYear()}-Q${quarterIdx + 1}`;

  // All-Flax (every outlet, ignoring the global selector scope) for the
  // "MTD Flax Sales — all cities" tile. Independent of `inScope`.
  const allOutletIds = new Set(outletsAll.map((o) => o.id));
  const inAllFlax = (r: DailyRow) => allOutletIds.has(r.outlet_id);
  let flaxMtdNet = 0;
  let flaxLmAtSamePoint = 0;
  let flaxLyAtSamePoint = 0;
  let flaxLyFullMonth = 0;
  const mtdOutlets = new Set<string>();
  const lmOutlets = new Set<string>();
  const lyOutlets = new Set<string>();
  for (const r of rows) {
    if (!inAllFlax(r)) continue;
    const pd = parseRowDate(r.date);
    const v = netOf(r);
    if (pd.y === todayY && pd.m === todayM && pd.d <= daysElapsed) {
      flaxMtdNet += v;
      if (v > 0) mtdOutlets.add(r.outlet_id);
    }
    if (pd.y === lmY && pd.m === lmM && pd.d <= daysElapsed) {
      flaxLmAtSamePoint += v;
      if (v > 0) lmOutlets.add(r.outlet_id);
    }
    if (pd.y === lyY && pd.m === lyM) {
      flaxLyFullMonth += v;
      if (pd.d <= daysElapsed) flaxLyAtSamePoint += v;
      if (v > 0) lyOutlets.add(r.outlet_id);
    }
  }

  // Per outlet — yesterday vs SDLW + MTD AOV + MTD discount/addon
  // + last-month-at-same-point (so each city card can show vs-LM-MTD).
  const perOutlet = outlets.map((o) => {
    const yRow = get(o.id, yest);
    const sRow = get(o.id, sdlw);
    let ordersMtd = 0;
    let netMtd = 0;
    let discountMtd = 0;
    let addonMtd = 0;
    let netLmAtSamePoint = 0;
    for (const r of rows) {
      if (r.outlet_id !== o.id) continue;
      const pd = parseRowDate(r.date);
      if (pd.y === todayY && pd.m === todayM && pd.d <= daysElapsed) {
        ordersMtd += Number(r.orders ?? 0);
        netMtd += netOf(r);
        discountMtd += Number(r.discount_total ?? 0);
        addonMtd += Number(r.addon_revenue ?? 0);
      }
      if (pd.y === lmY && pd.m === lmM && pd.d <= daysElapsed) {
        netLmAtSamePoint += netOf(r);
      }
    }
    return {
      outletId: o.id,
      netSalesYest: netOf(yRow),
      netSalesSdlw: netOf(sRow),
      deltaVsSdlw: netOf(yRow) - netOf(sRow),
      ordersMtd,
      netSalesMtd: netMtd,
      aovMtd: ordersMtd > 0 ? netMtd / ordersMtd : 0,
      discountMtd,
      addonMtd,
      netLmAtSamePoint,
    };
  });

  return {
    today,
    yest: { date: yest, netSales: yestSum },
    sdlw: { date: sdlw, netSales: sdlwSum },
    sdlm: { date: sdlm, netSales: sdlmSum },
    mtd: {
      netSales: mtdNet,
      orders: mtdOrders,
      covers: mtdCovers,
      discountTotal: mtdDiscount,
      addonRevenue: mtdAddon,
      addonOrders: mtdAddonOrders,
      dineinOrders: mtdDinein,
      deliveryOrders: mtdDelivery,
      daysElapsed,
      daysInMonth,
      projected,
    },
    qtd: {
      netSales: qtdNet,
      quarter: quarterKey,
      daysElapsed: daysIntoQuarter,
      daysInQuarter,
      projected: qtdProjected,
    },
    lastQTDAtSamePoint,
    lastQuarterTotal,
    ytd: {
      netSales: ytdNet,
      year: today.getFullYear(),
      daysElapsed: daysIntoYear,
      daysInYear,
      projected: ytdProjected,
    },
    monthKey,
    yearKey,
    quarterKey,
    lastMonthAtSamePoint,
    scopedLyAtSamePoint,
    scopedLyFullMonth,
    outletCounts: {
      mtd: mtdOutlets.size,
      lm: lmOutlets.size,
      ly: lyOutlets.size,
    },
    flaxAll: {
      mtdNet: flaxMtdNet,
      lmAtSamePoint: flaxLmAtSamePoint,
      lyAtSamePoint: flaxLyAtSamePoint,
      lyFullMonth: flaxLyFullMonth,
    },
    perOutlet,
  };
}

// ───────── Date utils (IST-pinned simple helpers) ─────────

function anchorDateForPeriod(period: string, customEnd?: string): Date {
  const real = todayISTDate();
  if (period === 'last_month') {
    return new Date(real.getFullYear(), real.getMonth(), 0);
  }
  if (period === 'last_year') {
    // Dec 31 of the previous calendar year. MTD → December, QTD → Q4,
    // YTD → the full previous year. Year-over-year comparison rebases to
    // the year before that.
    return new Date(real.getFullYear() - 1, 11, 31);
  }
  if (period === 'yesterday') {
    return new Date(real.getFullYear(), real.getMonth(), real.getDate() - 1);
  }
  if (period === 'custom' && customEnd) {
    const [y, m, d] = customEnd.split('-').map(Number);
    if (y && m && d) return new Date(y, m - 1, d);
  }
  return real;
}

function todayISTDate(): Date {
  // IST = UTC+5:30. We want the calendar date in IST as a local Date object
  // so .getDate(), .getMonth() etc reflect IST.
  const now = new Date();
  const istMs = now.getTime() + 5.5 * 3600 * 1000;
  const ist = new Date(istMs);
  return new Date(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate());
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function sameDayLastMonth(d: Date): Date {
  const target = new Date(d);
  target.setMonth(target.getMonth() - 1);
  // If day overflows (e.g. May 31 -> Apr 31 doesn't exist), JS snaps to May 1.
  // Pull back to the last valid day of the previous month.
  if (target.getMonth() !== (d.getMonth() + 11) % 12) {
    target.setDate(0);
  }
  return target;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const ms = Date.now() - then;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
